import { useEffect, useState } from "react";
import { X, Droplets, ExternalLink, Ship, Building2, AlertTriangle, Loader2, Radio } from "lucide-react";
import { CATEGORY_HEX, categoryLabel, timeAgo } from "@/lib/maritime";
import api from "@/lib/api";

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
    <span className="text-[11px] font-mono uppercase tracking-wider text-slate-500">{label}</span>
    <span className="text-sm text-slate-200 font-medium text-right">{value ?? "—"}</span>
  </div>
);

export const SlickPanel = ({ slickId, onClose }) => {
  const [d, setD] = useState(null);
  const [corr, setCorr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setD(null);
    setCorr(null);
    setLoading(true);
    (async () => {
      try {
        const detail = await api.slickDetail(slickId);
        if (live) setD(detail);
        const c = await api.slickCorrelate(slickId);
        if (live) setCorr(c);
      } catch { /* ignore */ }
      if (live) setLoading(false);
    })();
    return () => { live = false; };
  }, [slickId]);

  const candidates = corr?.candidates || [];

  return (
    <aside
      data-testid="slick-detail-panel"
      className="absolute top-16 right-4 bottom-16 z-20 w-80 md:w-96 flex flex-col rounded-xl bg-slate-900/95 backdrop-blur-xl border border-[#FF2A6D]/30 shadow-2xl overflow-hidden st-fade"
    >
      <div className="flex items-start justify-between p-4 border-b border-slate-800/80">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Droplets className="h-4 w-4 text-[#FF2A6D]" />
            <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#FF2A6D]/15 text-[#FF6B9D]">
              Potential Slick
            </span>
            {d?.cls_name && (
              <span
                data-testid="slick-classification"
                className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-cyan-500/15 text-cyan-300"
              >
                {d.cls_name}
              </span>
            )}
          </div>
          <h2 className="font-display text-lg font-bold text-white truncate">{d?.cerulean_id || "…"}</h2>
          <p className="font-mono text-[11px] text-slate-500 mt-0.5">
            {d?.image_ts ? new Date(d.image_ts).toISOString().slice(0, 16).replace("T", " ") + " UTC" : ""}
          </p>
        </div>
        <button data-testid="slick-panel-close" onClick={onClose} className="text-slate-500 hover:text-slate-200 shrink-0">
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto st-scroll p-4 space-y-4">
        <div className="rounded-lg bg-slate-800/40 border border-slate-700/50 px-3 py-1">
          <Row label="Area" value={d?.area_km2 != null ? `${d.area_km2} km²` : "—"} />
          <Row label="Slick confidence" value={d?.slick_confidence != null ? `${Math.round(d.slick_confidence * 100)}%` : "—"} />
          <Row label="Machine confidence" value={d?.machine_confidence != null ? `${Math.round(d.machine_confidence * 100)}%` : "—"} />
          <Row label="Sentinel-1 scene" value={d?.scene_id} />
        </div>

        <div data-testid="slick-cerulean-sources">
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] uppercase tracking-widest text-[#FF6B9D]">Cerulean likely source</span>
            {d && <span className="font-mono text-[11px] text-slate-500">{(d.sources || []).length}</span>}
          </div>
          {d && (d.sources || []).length === 0 && (
            <p data-testid="slick-no-sources" className="text-center text-xs text-slate-500 py-4 font-mono">
              No source attribution published for this detection yet.
            </p>
          )}
          <div className="space-y-1.5">
            {(d?.sources || []).slice(0, 12).map((s, i) => {
              const Icon = s.kind === "vessel" ? Ship : s.kind === "infrastructure" ? Building2 : Radio;
              const label =
                s.kind === "vessel" ? `Vessel · MMSI ${s.identifier}`
                : s.kind === "infrastructure" ? `Infrastructure ${s.identifier}`
                : s.kind === "dark" ? `Dark target ${s.identifier}`
                : `${s.source_type || "Source"} ${s.identifier}`;
              return (
                <div
                  key={`${s.identifier}-${i}`}
                  data-testid={`cerulean-source-${i}`}
                  className="rounded-lg border border-slate-800/70 bg-slate-800/40 px-3 py-2 flex items-center gap-2"
                >
                  <Icon className={`h-3.5 w-3.5 shrink-0 ${s.kind === "vessel" ? "text-[#FF6B9D]" : s.kind === "infrastructure" ? "text-amber-400" : "text-slate-400"}`} />
                  <span className="text-sm text-slate-200 font-medium truncate flex-1">{label}</span>
                  <span className="font-mono text-[10px] text-slate-500 shrink-0">
                    #{s.rank ?? "—"}{s.collated_score != null ? ` · ${Number(s.collated_score).toFixed(2)}` : ""}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {d?.slick_url && (
          <a
            href={d.slick_url}
            target="_blank"
            rel="noreferrer"
            data-testid="slick-cerulean-link"
            className="flex items-center justify-center gap-2 rounded-lg border border-[#FF2A6D]/40 bg-[#FF2A6D]/10 px-3 py-2 text-xs font-mono text-[#FF6B9D] hover:bg-[#FF2A6D]/20 transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Open in Cerulean
          </a>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[11px] uppercase tracking-widest text-cyan-400">SlickTrace AIS proximity</span>
            {corr && (
              <span className="font-mono text-[11px] text-slate-500">
                {corr.vessels.length}v · {corr.infrastructure.length}i
              </span>
            )}
          </div>
          <p className="text-[10px] text-slate-500 font-mono mb-2">
            Within {corr?.buffer_km ?? 10} km · {corr ? `${new Date(corr.window_start).toISOString().slice(5,16).replace("T"," ")} → ${new Date(corr.window_end).toISOString().slice(5,16).replace("T"," ")}` : "…"}
          </p>

          {loading && (
            <div className="flex items-center gap-2 text-slate-500 text-xs py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Correlating stored AIS + infrastructure…
            </div>
          )}

          {!loading && candidates.length === 0 && (
            <p data-testid="slick-no-candidates" className="text-center text-xs text-slate-500 py-6 font-mono">
              No vessels or structures in the buffer during the window.
            </p>
          )}

          <div className="space-y-1.5">
            {candidates.slice(0, 60).map((c) => (
              <div
                key={c.type + (c.mmsi || c.gfw_id)}
                data-testid={`candidate-${c.type}-${c.mmsi || c.gfw_id}`}
                className="rounded-lg border border-slate-800/70 bg-slate-800/40 px-3 py-2"
              >
                <div className="flex items-center gap-2">
                  {c.type === "vessel" ? (
                    <Ship className="h-3.5 w-3.5 shrink-0" style={{ color: CATEGORY_HEX[c.category] }} />
                  ) : (
                    <Building2 className="h-3.5 w-3.5 text-amber-400 shrink-0" />
                  )}
                  <span className="text-sm text-slate-200 font-medium truncate flex-1">
                    {c.type === "vessel" ? (c.name || "Unknown vessel") : (c.field || "Structure")}
                  </span>
                  <span className="font-mono text-[11px] text-cyan-400 tabular-nums">{c.distance_km} km</span>
                </div>
                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                  <span className="font-mono text-[10px] text-slate-500">
                    {c.type === "vessel" ? `MMSI ${c.mmsi} · ${categoryLabel(c.category)}` : `GFW ${c.gfw_id}`}
                  </span>
                  {c.in_cerulean_candidate && (
                    <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-[#FF2A6D]/15 text-[#FF6B9D]">
                      Cerulean candidate
                    </span>
                  )}
                  {c.type === "vessel" && c.ais_gap && (
                    <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-amber-500/15 text-amber-400 flex items-center gap-0.5">
                      <AlertTriangle className="h-2.5 w-2.5" /> AIS gap
                    </span>
                  )}
                  {c.type === "infrastructure" && c.slick_history_count > 1 && (
                    <span className="text-[9px] font-mono uppercase px-1 py-0.5 rounded bg-red-500/15 text-red-400">
                      Chronic ×{c.slick_history_count}
                    </span>
                  )}
                </div>
                {c.type === "vessel" && c.closest_approach && (
                  <p className="text-[10px] text-slate-500 font-mono mt-0.5">
                    Closest {timeAgo(c.closest_approach)}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg bg-slate-800/30 border border-dashed border-slate-700/50 p-3 flex items-start gap-2">
          <Radio className="h-4 w-4 text-[#FF2A6D]/70 mt-0.5 shrink-0" />
          <p className="text-[11px] leading-relaxed text-slate-500">
            Radar alone cannot confirm oil — all detections are <b>potential slicks</b> requiring expert review.
            Candidates are proximity matches, not proof of causation.
          </p>
        </div>
      </div>
    </aside>
  );
};

export default SlickPanel;
